import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import Modal from "@/store/components/Modal";
import Button from "@/store/components/Button";

interface ConfirmOptions {
  title: string;
  /** Body content (may include markup via ReactNode). */
  body: ReactNode;
  confirmLabel?: string;
}

type Confirm = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Confirm | null>(null);

/** Access an imperative `confirm({ title, body })` that resolves to the user's choice. */
export function useConfirm(): Confirm {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within <ConfirmProvider>");
  return ctx;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<Confirm>((opts) => {
    setOptions(opts);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((value: boolean) => {
    resolver.current?.(value);
    resolver.current = null;
    setOptions(null);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {options ? (
        <Modal
          title={options.title}
          onClose={() => settle(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => settle(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => settle(true)}>
                {options.confirmLabel ?? "Confirm"}
              </Button>
            </>
          }
        >
          <p className="agd:m-0 agd:py-2 agd:text-sm agd:leading-relaxed agd:text-[#4a5568]">{options.body}</p>
        </Modal>
      ) : null}
    </ConfirmContext.Provider>
  );
}
